import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  Filter, 
  BookOpen, 
  Clock, 
  Users, 
  Star,
  MapPin,
  Calendar,
  Building2,
  Sparkles,
  Target,
  Zap,
  Eye,
  ArrowRight,
  Award,
  CheckCircle,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { formationsService, Formation } from '../../services/formations.service';
import { useNavigate } from 'react-router-dom';

const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');

  // Categories based on specialités
  const categories = [
    { id: 'all', name: t('trainee.catalog.categories.all'), count: 0 },
    { id: 'pharmacologie', name: t('trainee.catalog.categories.pharmacology'), count: 0 },
    { id: 'legislation', name: t('trainee.catalog.categories.legislation'), count: 0 },
    { id: 'gestion', name: t('trainee.catalog.categories.management'), count: 0 },
    { id: 'dispensation', name: t('trainee.catalog.categories.dispensation'), count: 0 },
    { id: 'recherche', name: t('trainee.catalog.categories.research'), count: 0 },
    { id: 'pharmacovigilance', name: t('trainee.catalog.categories.pharmacovigilance'), count: 0 }
  ];

  const formats = [
    { id: 'all', name: t('trainee.catalog.formats.all') },
    { id: 'presentiel', name: t('trainee.catalog.formats.presentiel') },
    { id: 'en_ligne', name: t('trainee.catalog.formats.en_ligne') },
    { id: 'hybride', name: t('trainee.catalog.formats.hybride') }
  ];

  useEffect(() => {
    loadFormations();
  }, []);

  const loadFormations = async () => {
    try {
      setLoading(true);
      const data = await formationsService.getFormations();
      setFormations(data);
      
      // Update category counts
      const updatedCategories = categories.map(category => {
        if (category.id === 'all') {
          return { ...category, count: data.length };
        }
        const count = data.filter(formation => 
          formation.specialites.some(specialite => 
            specialite.toLowerCase().includes(category.id)
          )
        ).length;
        return { ...category, count };
      });
      
      // Update the categories state if needed
      categories.splice(0, categories.length, ...updatedCategories);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredFormations = formations.filter(formation => {
    const matchesSearch = formation.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         formation.centre.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         formation.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      formation.specialites.some(specialite => 
        specialite.toLowerCase().includes(selectedCategory)
      );
    
    const matchesFormat = selectedFormat === 'all' || formation.format === selectedFormat;
    
    return matchesSearch && matchesCategory && matchesFormat;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getFormatColor = (format: string) => {
    switch (format) {
      case 'presentiel':
        return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      case 'en_ligne':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'hybride':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFormatText = (format: string) => {
    switch (format) {
      case 'presentiel':
        return t('trainee.catalog.formatText.presentiel');
      case 'en_ligne':
        return t('trainee.catalog.formatText.en_ligne');
      case 'hybride':
        return t('trainee.catalog.formatText.hybride');
      default:
        return t('trainee.catalog.formatText.unknown');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'en_attente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('trainee.catalog.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('trainee.catalog.title')}>
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-br from-brand-primary/5 via-brand-accent/5 to-brand-surface rounded-3xl p-4 sm:p-8 border border-brand-primary/20 shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
                <div className="p-3 sm:p-4 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-2xl shadow-lg self-start">
                  <BookOpen className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-brand-primary to-brand-accent bg-clip-text text-transparent">
                    {t('trainee.catalog.title')} 📚
                  </h2>
                  <p className="text-gray-600 mt-2 text-base sm:text-lg">
                    {t('trainee.catalog.subtitle', { count: formations.length })}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-6">
                <div className="flex items-center space-x-2 text-sm text-gray-600 bg-white/50 px-3 py-2 rounded-lg">
                  <Target className="h-4 w-4" />
                  <span>{t('trainee.catalog.availableTrainings', { count: formations.length })}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600 bg-white/50 px-3 py-2 rounded-lg">
                  <Building2 className="h-4 w-4" />
                  <span>{t('trainee.catalog.trainingCenters', { count: new Set(formations.map(f => f.centre.id)).size })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-3xl border border-gray-200 p-4 sm:p-6 shadow-xl">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('trainee.catalog.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
              >
                {formats.map(format => (
                  <option key={format.id} value={format.id}>
                    {format.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-gray-600 text-base sm:text-lg">
            {t('trainee.catalog.resultsCount', { count: filteredFormations.length })}
          </p>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
            <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{t('trainee.catalog.popularTrainings')}</span>
          </div>
        </div>

        {/* Formations Grid */}
        {filteredFormations.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {filteredFormations.map((formation, index) => (
              <motion.div
                key={formation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:border-brand-primary/30 cursor-pointer"
              >
                {/* Image */}
                <div className="h-48 bg-gray-200 relative">
                  <img
                    src={formation.visuel || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop'}
                    alt={formation.titre}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getFormatColor(formation.format)}`}>
                      {getFormatText(formation.format)}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-black/70 text-white text-sm font-medium rounded-lg">
                      {formatCurrency(formation.prix)}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(formation.statut)}`}>
                      {formation.statut === 'active' ? t('trainee.catalog.status.active') : formation.statut}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4 text-brand-primary" />
                    <span className="text-sm text-gray-600 font-medium">{formation.centre.nom}</span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-brand-primary transition-colors">
                    {formation.titre}
                  </h3>

                  <p className="text-gray-600 mb-6 line-clamp-2">
                    {formation.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-gradient-to-br from-brand-primary/5 to-brand-primary/10 rounded-2xl">
                      <div className="text-2xl font-bold text-brand-primary">{formation.duree}</div>
                      <div className="text-sm text-gray-600 font-medium">{t('trainee.catalog.stats.days')}</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-brand-accent/5 to-brand-accent/10 rounded-2xl">
                      <div className="text-2xl font-bold text-brand-accent">{formation.nombreVues}</div>
                      <div className="text-sm text-gray-600 font-medium">{t('trainee.catalog.stats.views')}</div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('trainee.catalog.details.averageRating')}</span>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="font-semibold text-gray-900">
                          {typeof formation.noteMoyenne === 'number' ? formation.noteMoyenne.toFixed(1) : '0.0'}
                        </span>
                        <span className="text-gray-500">({formation.nombreAvis} {t('trainee.catalog.details.reviews')})</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('trainee.catalog.details.targetAudience')}</span>
                      <span className="font-semibold text-gray-900">{formation.publicCible}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('trainee.catalog.details.prerequisites')}</span>
                      <span className="font-semibold text-gray-900">{formation.prerequis}</span>
                    </div>
                  </div>

                  {/* Specialités */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {formation.specialites.slice(0, 3).map((specialite, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs font-medium rounded-full"
                        >
                          {specialite}
                        </span>
                      ))}
                      {formation.specialites.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          +{formation.specialites.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Next Session */}
                  {formation.sessions.length > 0 && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-brand-primary/5 to-brand-accent/5 rounded-2xl border border-brand-primary/20">
                      <div className="flex items-center gap-3 text-brand-primary">
                        <Calendar className="h-5 w-5" />
                        <div>
                          <span className="text-sm font-semibold">{t('trainee.catalog.nextSession')}</span>
                          <div className="text-lg font-bold">
                            {new Date(formation.sessions[0].dateDebut).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button 
                      onClick={() => navigate(`/training-subscription/${formation.id}`)}
                      className="flex-1 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white py-3 px-6 rounded-xl font-semibold hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                      <Eye className="h-5 w-5 mr-2 inline" />
                      {t('trainee.catalog.actions.viewDetails')}
                    </button>
                    <button 
                      onClick={() => navigate(`/training-subscription/${formation.id}`)}
                      className="px-6 py-3 border-2 border-brand-accent text-brand-accent rounded-xl font-semibold hover:bg-brand-accent hover:text-white transition-all flex items-center gap-2"
                    >
                      <BookOpen className="h-5 w-5" />
                      {t('trainee.catalog.actions.enroll')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          /* No results */
          <div className="text-center py-16 bg-white rounded-3xl border border-gray-200 shadow-xl">
            <div className="p-6 bg-gradient-to-br from-brand-primary/10 to-brand-accent/10 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <BookOpen className="h-12 w-12 text-brand-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t('trainee.catalog.noResults.title')}
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">
              {searchTerm || selectedCategory !== 'all' || selectedFormat !== 'all'
                ? t('trainee.catalog.noResults.description')
                : t('trainee.catalog.noResults.noTrainingsAvailable')
              }
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedFormat('all');
              }}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white font-semibold rounded-2xl hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all shadow-lg"
            >
              <Search className="h-6 w-6 mr-3" />
              {t('trainee.catalog.noResults.viewAllTrainings')}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CatalogPage;
